import {
  Box,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { ResponseNumberSubTypeEnum } from '@ulysses-inc/harami_api_client'
import type { FormikProps } from 'formik'
import type { ChangeEvent, FC } from 'react'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import { SubTypeText } from 'src/constants/responseNumber'
import type { NumberRuleProps } from './EditResponseNumberRuleDrawer'

const NormalValueRow: FC<{
  formikProps: FormikProps<NumberRuleProps>
}> = ({
  formikProps: {
    values: { hasNumberRule, subType, minimum, maximum, scale },
    handleChange,
    setFieldValue,
    errors,
    touched,
  },
}) => {
  const update = (key: keyof NumberRuleProps) => (value: unknown) =>
    setFieldValue(key, value)

  const onChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!checked) {
      update('minimum')(undefined)
      update('maximum')(undefined)
      update('scale')(undefined)
      update('subType')(undefined)
    }
    update('hasNumberRule')(checked)
  }

  return (
    <Stack>
      <FormControlLabel
        control={<Switch checked={hasNumberRule} onChange={onChange} />}
        label="正常値を設定する"
        sx={{ mb: 1 }}
      />
      {hasNumberRule && (
        <Stack direction="row" alignItems="center">
          {subType !== ResponseNumberSubTypeEnum.LESS_THAN && (
            <Box sx={{ width: 90 }}>
              <TextField
                type="number"
                size="small"
                name="minimum"
                placeholder="0"
                value={minimum ?? ''}
                onChange={e =>
                  update('minimum')(
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
                slotProps={{ htmlInput: { 'aria-label': '正常値の下限値' } }}
                fullWidth
              />
            </Box>
          )}
          {(!subType || subType === ResponseNumberSubTypeEnum.BETWEEN) && (
            <Box sx={{ width: 35, textAlign: 'center' }}>
              <Typography>ー</Typography>
            </Box>
          )}
          {subType !== ResponseNumberSubTypeEnum.GREATER_THAN && (
            <Box sx={{ width: 90 }}>
              <TextField
                type="number"
                size="small"
                name="maximum"
                placeholder="100"
                value={maximum ?? ''}
                onChange={e =>
                  update('maximum')(
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
                slotProps={{ htmlInput: { 'aria-label': '正常値の上限値' } }}
                fullWidth
              />
            </Box>
          )}
          <Box sx={{ width: 90, ml: 2 }}>
            <TextField
              size="small"
              name="scale"
              placeholder="℃"
              value={scale ?? ''}
              onChange={handleChange}
              slotProps={{ htmlInput: { 'aria-label': '正常値の単位' } }}
              fullWidth
            />
          </Box>
          <Box sx={{ width: 90 }}>
            <Select<ResponseNumberSubTypeEnum | ''>
              displayEmpty
              size="small"
              value={subType ?? ''}
              onChange={e => update('subType')(e.target.value || undefined)}
              inputProps={{ 'aria-label': '正常値の範囲指定' }}
              renderValue={value =>
                value === '' ? (
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    の間
                  </Box>
                ) : (
                  SubTypeText[value]
                )
              }
              fullWidth
            >
              <MenuItem
                value={ResponseNumberSubTypeEnum.BETWEEN}
                data-testid="select-number-subtype-item"
              >
                {SubTypeText[ResponseNumberSubTypeEnum.BETWEEN]}
              </MenuItem>
              <MenuItem
                value={ResponseNumberSubTypeEnum.GREATER_THAN}
                data-testid="select-number-subtype-item"
              >
                {SubTypeText[ResponseNumberSubTypeEnum.GREATER_THAN]}
              </MenuItem>
              <MenuItem
                value={ResponseNumberSubTypeEnum.LESS_THAN}
                data-testid="select-number-subtype-item"
              >
                {SubTypeText[ResponseNumberSubTypeEnum.LESS_THAN]}
              </MenuItem>
            </Select>
          </Box>
        </Stack>
      )}
      <Stack>
        <FormikErrorMessage name="subType" errors={errors} touched={touched} />
        <FormikErrorMessage name="maximum" errors={errors} touched={touched} />
        <FormikErrorMessage name="minimum" errors={errors} touched={touched} />
      </Stack>
    </Stack>
  )
}

export default NormalValueRow
